// AppNav: the app's one sidebar, a warm-black column. Open, each mode is a row
// with its icon and name, the open mode's own pages sit under its row, a
// mode's list fills the room below, and Chat and the account are at the foot. Folded, it
// narrows to the icons, which stay where they were, so only its width changes
// (styles.css). App.jsx and the lanes harness draw the same column.
import { Fragment } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import SecurityShieldIcon from "./SecurityShieldIcon.jsx";
import { phAttrs } from "../lib/analytics/attrs.js";
import { ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";
import { ORIGIN_MARK_SVG, ORIGIN_WORDMARK_SVG } from "../lib/shell.js";

// The fold control: a panel with its sidebar ruled off.
function PanelIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2.5" width="12" height="11" rx="2" />
      <path d="M6.5 2.5v11" />
    </svg>
  );
}

// Per-mode icons. Same 16-grid + 1.4 stroke + round-caps family as the other
// sidebar icons, drawn in currentColor so a row's idle and chosen colors
// apply uniformly.
function WorkspaceIcon() {
  // A board: two cards laid on one clock.
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
      <path d="M4.5 6h5M6.5 9.5h5" />
    </svg>
  );
}

function ExploreIcon() {
  // The rings: the overview Explore opens on.
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="5.5" />
      <circle cx="8" cy="8" r="2.5" />
      <path d="M8 2.5v3M13.2 9.7l-2.8-.9" />
    </svg>
  );
}

function SettingsIcon() {
  // Two horizontal sliders with offset handles. Reads cleaner at
  // 14px than a gear (which loses its teeth) while still saying
  // "configuration / preferences."
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 5h11" />
      <circle cx="6" cy="5" r="1.5" />
      <path d="M2.5 11h11" />
      <circle cx="10" cy="11" r="1.5" />
    </svg>
  );
}

function EndpointsIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="3" width="11" height="8" rx="1.2" />
      <path d="M6 13h4M8 11v2" />
      <circle cx="11.2" cy="5.4" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  );
}

// A policy sheet with a check: the rules agents are held to.
function GovernanceIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 2.5h6l2.5 2.5v8.5H4Z" />
      <path d="M6.2 9.2l1.4 1.4 2.6-3" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13.5 7.5a5.5 5.5 0 0 1-5.5 5.5H2.5l1.2-3A5.5 5.5 0 1 1 13.5 7.5Z" />
      <path d="M5.5 6.5h5M5.5 9h3" />
    </svg>
  );
}

// Four panes of unequal height: a page of tiles, the Overview.
function OverviewIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="5" height="6.5" rx="1" />
      <rect x="9" y="2" width="5" height="3.5" rx="1" />
      <rect x="9" y="7.5" width="5" height="6.5" rx="1" />
      <rect x="2" y="10.5" width="5" height="3.5" rx="1" />
    </svg>
  );
}

function TriageIcon() {
  return <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h7M6 8h7M6 12h7"/><circle cx="2.5" cy="4" r=".6"/><circle cx="2.5" cy="8" r=".6"/><circle cx="2.5" cy="12" r=".6"/></svg>;
}

const MODE_ICONS = {
  overview: OverviewIcon,
  explore: ExploreIcon,
  workspace: WorkspaceIcon,
  dashboard: ChatIcon,
  triage: TriageIcon,
  security: SecurityShieldIcon,
  endpoints: EndpointsIcon,
  governance: GovernanceIcon,
  settings: SettingsIcon,
};

// `modes` are MODES entries (AdminMenu.jsx) the reader may open. `nested` is
// the current mode's own pages, `list` its list, `foot` the account. Both stay
// mounted while folded, as the drawer they replace did, and only hide.
export default function AppNav({ modes, current, open, onToggle, onSelect, onHome, nested = null, list = null, foot = null, onPick }) {
  const fold = open ? "Fold the sidebar" : "Open the sidebar";
  const row = (m) => {
    const Icon = MODE_ICONS[m.id];
    const active = m.id === current;
    return (
      <Fragment key={m.id}>
        {/* Named on hover only while folded: open, the row says it. */}
        <HoverTooltip label={open ? null : m.label}>
          <button
            type="button"
            data-origin-mode={m.id}
            data-active={active ? "true" : "false"}
            aria-current={active ? "page" : undefined}
            aria-label={m.label}
            data-drawer-pick=""
            {...phAttrs({ [ATTRIBUTE.NAV_MODE]: m.id })}
            onClick={() => onSelect(m.id)}
          >
            {Icon ? <Icon /> : <span aria-hidden="true">[{m.rail}]</span>}
            <span data-origin-mode-label="" aria-hidden="true">{m.label}</span>
          </button>
        </HoverTooltip>
        {active && nested ? <div data-nav-nested="">{nested}</div> : null}
      </Fragment>
    );
  };
  // Chat sits in the foot, over the notification bell.
  const chat = modes.find((m) => m.id === "dashboard");
  return (
    <>
      {/* On a phone the open column lies over the page, with this scrim over
          the rest of it: a tap folds the column (styles.css). */}
      <div data-sidebar-scrim aria-hidden="true" onClick={onToggle} />
      <aside data-origin-rail data-open={open ? "true" : "false"} onClick={onPick}>
        <div data-nav-head="">
          {/* Open, the wordmark goes home and the fold control rides the right
              edge. Folded, the [Ø] alone is the control, in the same corner:
              under the pointer or focus it turns into the panel it opens, so
              nothing among the modes' icons below looks like a mode. */}
          {open ? <button type="button" data-nav-home="" aria-label="Return to overview and reset panel layout" onClick={onHome} dangerouslySetInnerHTML={{ __html: ORIGIN_WORDMARK_SVG }} /> : null}
          <HoverTooltip label={fold}>
            <button type="button" data-nav-toggle="" aria-label={fold} aria-expanded={open} onClick={onToggle}>
              {open ? null : <span data-nav-mark="" aria-hidden="true" dangerouslySetInnerHTML={{ __html: ORIGIN_MARK_SVG }} />}
              <PanelIcon />
            </button>
          </HoverTooltip>
        </div>
        <nav data-origin-modes aria-label="Main navigation">
          {modes.filter((m) => m !== chat).map(row)}
        </nav>
        <div data-nav-list="">{list}</div>
        {foot || chat ? <div data-nav-foot="">{chat ? row(chat) : null}{foot}</div> : null}
      </aside>
    </>
  );
}
